import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  TextField,
  Grid,
  Alert,
  LinearProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import {useState, useEffect} from 'react';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import LicenseIcon from '@mui/icons-material/VerifiedUser';
import CalendarIcon from '@mui/icons-material/Schedule';
import PeopleIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import message from 'src/components/Message';
import {formatDate} from 'src/utils/common';
import {useOrgId} from 'src/hooks';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Import license API services and types
import {
  licenseServiceRedeem,
  licenseServiceGetAll,
  licenseServiceGetQuota,
} from 'src/apis/autoGenerate/services';
import {License, LicenseQuota, LicenseQuotaByType} from 'src/apis/autoGenerate/types/license';

export default function LicensePage() {
  const {orgId} = useOrgId();
  const [loading, setLoading] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [quota, setQuota] = useState<LicenseQuota | null>(null);
  const [licenses, setLicenses] = useState<License[]>([]);
  const [masterKey, setMasterKey] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState(false);

  // Load license data when component mounts
  useEffect(() => {
    if (orgId) {
      loadLicenseData();
    }
  }, [orgId]);

  // Function to load all license data
  const loadLicenseData = async () => {
    setLoading(true);
    try {
      const [quotaResponse, licensesResponse] = await Promise.all([
        licenseServiceGetQuota(),
        licenseServiceGetAll(),
      ]);

      if (quotaResponse.data?.data) {
        setQuota(quotaResponse.data.data);
      }

      if (licensesResponse.data?.data) {
        setLicenses(licensesResponse.data.data);
      }
    } catch (error: any) {
      console.error('Failed to load license data:', error);
      message.error('Failed to load license information');
    } finally {
      setLoading(false);
    }
  };

  // Function to redeem a license
  const handleRedeemLicense = async () => {
    if (!masterKey.trim()) {
      message.error('Please enter a master key');
      return;
    }

    setRedeemLoading(true);
    try {
      const response = await licenseServiceRedeem({token: masterKey.trim()});

      if (response.data?.message) {
        message.success(response.data.message);
        setRedeemSuccess(true);
        setMasterKey('');
        await loadLicenseData();
      }
    } catch (error: any) {
      console.error('Failed to redeem license:', error);
      message.error(error.response?.data?.message || 'Failed to redeem license');
    } finally {
      setRedeemLoading(false);
    }
  };

  // Check if quota is in old flat format (has total_units at top level)
  const isOldFormat = (quota: LicenseQuota | null): boolean => {
    if (!quota) return false;
    return 'total_units' in quota && typeof quota.total_units === 'number';
  };

  // Format license type name for display
  const formatLicenseTypeName = (type: string): string => {
    if (type.toLowerCase() === 'sip') {
      return 'SIP';
    }
    return type
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get license type chip color for visual distinction
  const getLicenseTypeChipColor = (licenseType: string) => {
    // use lowercase comparison
    if (licenseType.toLowerCase().includes('commercial')) {
      return {bg: '#EFF6FF', text: '#1D4ED8'};
    }
    if (
      licenseType.toLowerCase().includes('multi') ||
      licenseType.toLowerCase().includes('family')
    ) {
      return {bg: '#ECFDF3', text: '#15803D'};
    }
    if (licenseType.toLowerCase() === 'sip') {
      return {bg: '#FFF7ED', text: '#C2410C'};
    }
    return {bg: '#F3F4F6', text: '#374151'};
  };

  // Get all license types from quota (handles both old and new formats)
  const getLicenseTypes = (): string[] => {
    if (!quota) return [];

    // New nested format: types are keys in the quota object
    if (!isOldFormat(quota)) {
      return Object.keys(quota).filter(key => key !== 'organization_id');
    }

    // Old flat format: extract types from licenses array
    const typesSet = new Set<string>();
    licenses.forEach(license => {
      if (license.license_type) {
        typesSet.add(license.license_type);
      }
    });
    return Array.from(typesSet);
  };

  // Helper function to get quota by license type (handles both formats)
  const getQuotaByType = (licenseType: string): LicenseQuotaByType | null => {
    if (!quota) return null;

    // New nested format: get directly from quota object
    if (!isOldFormat(quota)) {
      const quotaData = quota[licenseType];
      if (quotaData && typeof quotaData === 'object' && 'total_units' in quotaData) {
        return quotaData as LicenseQuotaByType;
      }
      return null;
    }

    // Old flat format: calculate from licenses array
    const typeLicenses = licenses.filter(
      license => license.license_type === licenseType && license.license_status === 'active'
    );

    // Calculate stats for this license type
    const totalUnits = typeLicenses.reduce((sum, license) => sum + (license.units || 0), 0);

    // For old format, we need to estimate used/available from the overall quota
    // We'll distribute the used/available proportionally based on units
    const overallTotal = (quota.total_units as number) || 0;
    const overallUsed = (quota.used_units as number) || 0;

    // Calculate proportion for this type
    const proportion = overallTotal > 0 ? totalUnits / overallTotal : 0;
    const usedUnits = Math.round(overallUsed * proportion);
    const availableUnits = totalUnits - usedUnits;

    const hasUnlimited =
      quota.has_unlimited !== undefined && typeof quota.has_unlimited === 'boolean'
        ? quota.has_unlimited
        : false;

    return {
      total_units: totalUnits,
      used_units: usedUnits,
      available_units: availableUnits,
      has_unlimited: hasUnlimited,
      active_licenses_count: typeLicenses.length,
      licenses: typeLicenses.map(license => ({
        id: license.id,
        type: license.license_type,
        units: license.units,
        activated_at: license.activated_at,
        expires_at: license.license_expires_at,
      })),
    };
  };

  // Calculate aggregate stats across all license types (handles both formats)
  const getAggregateStats = () => {
    if (!quota) {
      return {
        totalUnits: 0,
        usedUnits: 0,
        availableUnits: 0,
        activeLicensesCount: 0,
        hasUnlimited: false,
      };
    }

    // Old flat format: use values directly from quota
    if (isOldFormat(quota)) {
      const hasUnlimited =
        quota.has_unlimited !== undefined && typeof quota.has_unlimited === 'boolean'
          ? quota.has_unlimited
          : false;

      return {
        totalUnits: (quota.total_units as number) || 0,
        usedUnits: (quota.used_units as number) || 0,
        availableUnits: (quota.available_units as number) || 0,
        activeLicensesCount: (quota.active_licenses_count as number) || 0,
        hasUnlimited,
      };
    }

    // New nested format: aggregate from license types. SIP is excluded because it
    // counts users rather than units, and would distort the unit usage percentage.
    const types = getLicenseTypes().filter(type => type.toLowerCase() !== 'sip');
    let totalUnits = 0;
    let usedUnits = 0;
    let availableUnits = 0;
    let activeLicensesCount = 0;
    let hasUnlimited = false;

    types.forEach(type => {
      const typeQuota = getQuotaByType(type);
      if (typeQuota) {
        totalUnits += typeQuota.total_units || 0;
        usedUnits += typeQuota.used_units || 0;
        availableUnits += typeQuota.available_units || 0;
        activeLicensesCount += typeQuota.active_licenses_count || 0;
        if (typeQuota.has_unlimited) {
          hasUnlimited = true;
        }
      }
    });

    return {
      totalUnits,
      usedUnits,
      availableUnits,
      activeLicensesCount,
      hasUnlimited,
    };
  };

  // Function to get status display
  const getStatusDisplay = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return {
          color: 'success' as const,
          icon: <CheckCircleIcon fontSize="small" />,
          label: 'Active',
        };
      case 'expired':
        return {
          color: 'error' as const,
          icon: <ErrorIcon fontSize="small" />,
          label: 'Expired',
        };
      case 'pending':
        return {
          color: 'warning' as const,
          icon: <WarningIcon fontSize="small" />,
          label: 'Pending',
        };
      default:
        return {
          color: 'default' as const,
          icon: <WarningIcon fontSize="small" />,
          label: 'Inactive',
        };
    }
  };

  // Calculate aggregate stats for alerts
  const aggregateStats = getAggregateStats();
  const usagePercentage =
    aggregateStats.totalUnits > 0
      ? (aggregateStats.usedUnits / aggregateStats.totalUnits) * 100
      : 0;
  const isNearlyFull = usagePercentage >= 90;

  return (
    <Box sx={{bgcolor: '#F9FAFB', minHeight: '100vh', p: 0, m: -5}}>
      <Box sx={{p: 5}}>
        {/* Header */}
        <Box sx={{mb: 4}}>
          <Typography variant="h4" sx={{color: '#111827', fontWeight: 700, mb: 1}}>
            License Management
          </Typography>
          <Typography variant="body1" sx={{color: '#6B7280', fontSize: 16}}>
            Monitor your organization's license usage and activate new licenses
          </Typography>
        </Box>

        {/* Critical Usage Alert */}
        {isNearlyFull && !aggregateStats.hasUnlimited && (
          <Alert
            severity="error"
            sx={{
              mb: 4,
              borderRadius: 3,
              bgcolor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              '& .MuiAlert-icon': {color: '#DC2626'},
              '& .MuiAlert-message': {color: '#991B1B', fontWeight: 500},
            }}
          >
            License usage is at {Math.round(usagePercentage)}%. Consider adding more licenses to
            avoid service interruption.
          </Alert>
        )}

        {/* Success Alert */}
        {redeemSuccess && (
          <Alert
            severity="success"
            onClose={() => setRedeemSuccess(false)}
            sx={{
              mb: 4,
              borderRadius: 3,
              bgcolor: '#F0FDF4',
              border: '1px solid #BBF7D0',
              '& .MuiAlert-icon': {color: '#059669'},
              '& .MuiAlert-message': {color: '#065F46', fontWeight: 500},
            }}
          >
            License activated successfully! Your quota has been updated.
          </Alert>
        )}

        {/* Global Summary */}
        <Grid container spacing={2} sx={{mb: 2}}>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid #E5E7EB',
                bgcolor: 'white',
                '&:hover': {
                  boxShadow: '0 2px 4px 0 rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <Box sx={{display: 'flex', justifyContent: 'center', mb: 1}}>
                <Avatar
                  sx={{
                    bgcolor: '#F3F4F6',
                    color: '#6B7280',
                    width: 28,
                    height: 28,
                  }}
                >
                  <LicenseIcon fontSize="small" />
                </Avatar>
              </Box>
              <Typography
                variant="h5"
                sx={{
                  color: '#111827',
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                {aggregateStats.totalUnits}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#6B7280',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                }}
              >
                Total Units
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid #E5E7EB',
                bgcolor: 'white',
                '&:hover': {
                  boxShadow: '0 2px 4px 0 rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <Box sx={{display: 'flex', justifyContent: 'center', mb: 1}}>
                <Avatar
                  sx={{
                    bgcolor: '#F3F4F6',
                    color: '#6B7280',
                    width: 28,
                    height: 28,
                  }}
                >
                  <PeopleIcon fontSize="small" />
                </Avatar>
              </Box>
              <Typography
                variant="h5"
                sx={{
                  color: '#111827',
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                {aggregateStats.usedUnits}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#6B7280',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                }}
              >
                Used Users
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{
                p: 1.5,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px solid #E5E7EB',
                bgcolor: 'white',
                '&:hover': {
                  boxShadow: '0 2px 4px 0 rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <Box sx={{display: 'flex', justifyContent: 'center', mb: 1}}>
                <Avatar
                  sx={{
                    bgcolor: '#F3F4F6',
                    color: '#6B7280',
                    width: 28,
                    height: 28,
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </Avatar>
              </Box>
              <Typography
                variant="h5"
                sx={{
                  color: aggregateStats.availableUnits < 0 ? '#DC2626' : '#111827',
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                {aggregateStats.availableUnits}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#6B7280',
                  fontWeight: 500,
                  fontSize: '0.7rem',
                }}
              >
                Remaining Units
              </Typography>
              {aggregateStats.availableUnits < 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#DC2626',
                    fontSize: '0.65rem',
                    display: 'block',
                    mt: 0.5,
                  }}
                >
                  Over limit overall
                </Typography>
              )}
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {/* License Quota Overview */}
          <Grid item xs={12} lg={8}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                border: '1px solid #E5E7EB',
                '&:hover': {
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <CardContent sx={{p: 2.5}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5}}>
                  <Avatar sx={{bgcolor: '#EFF6FF', color: '#3B82F6', width: 36, height: 36}}>
                    <LicenseIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{color: '#111827', fontWeight: 600, mb: 0.25}}>
                      License Overview
                    </Typography>
                    <Typography variant="body2" sx={{color: '#6B7280', fontSize: '0.8rem'}}>
                      Current usage and availability
                    </Typography>
                  </Box>
                  <Box sx={{ml: 'auto'}}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={loadLicenseData}
                      disabled={loading}
                      startIcon={
                        loading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />
                      }
                      sx={{
                        borderColor: '#E5E7EB',
                        color: '#6B7280',
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.5,
                        '&:hover': {borderColor: '#D1D5DB'},
                      }}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>

                {loading ? (
                  <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress sx={{color: '#3B82F6'}} />
                  </Box>
                ) : quota ? (
                  <Box>
                    {/* License Type Breakdown */}
                    {getLicenseTypes().length > 0 ? (
                      <Box>
                        <Grid container spacing={3}>
                          {getLicenseTypes().map(licenseType => {
                            const typeQuota = getQuotaByType(licenseType);
                            if (!typeQuota) return null;

                            const typeUsagePercentage =
                              (typeQuota.total_units || 0) > 0
                                ? ((typeQuota.used_units || 0) / (typeQuota.total_units || 1)) * 100
                                : 0;
                            const typeRemainingUnits = typeQuota.available_units || 0;
                            const typeIsNearlyFull = typeUsagePercentage >= 90;
                            const typeIsOverLimit = typeRemainingUnits < 0;
                            const chipColors = getLicenseTypeChipColor(licenseType);
                            const isSipType = licenseType.toLowerCase() === 'sip';

                            return (
                              <Grid item xs={12} md={6} lg={6} key={licenseType}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    borderRadius: 2,
                                    border: '1px solid #E5E7EB',
                                    bgcolor: 'white',
                                    p: 2.5,
                                    minWidth: 0,
                                    '&:hover': {
                                      boxShadow: '0 2px 4px 0 rgb(0 0 0 / 0.1)',
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      mb: 2.5,
                                    }}
                                  >
                                    <Box>
                                      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                        <Typography
                                          variant="h6"
                                          sx={{color: '#111827', fontWeight: 600, mb: 0.25}}
                                        >
                                          {formatLicenseTypeName(licenseType)}
                                        </Typography>
                                        {licenseType.toLowerCase() === 'sip' && (
                                          <Tooltip
                                            title={
                                              <Box>
                                                <Typography
                                                  variant="body2"
                                                  sx={{fontWeight: 600, mb: 1, color: 'white'}}
                                                >
                                                  SIP
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{mb: 1.5, color: 'white'}}
                                                >
                                                  SIP (Session Initiation Protocol) allows the
                                                  intercom to call users through phone or VoIP
                                                  systems instead of the mobile app.
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  sx={{fontWeight: 600, mb: 0.5, color: 'white'}}
                                                >
                                                  SIP License
                                                </Typography>
                                                <Typography variant="body2" sx={{color: 'white'}}>
                                                  A SIP license is required for each user who
                                                  receives SIP calls. 1 license applies to 1 user
                                                  (Resident User or Org User).
                                                </Typography>
                                              </Box>
                                            }
                                            arrow
                                            placement="top"
                                            sx={{
                                              '& .MuiTooltip-tooltip': {
                                                maxWidth: 400,
                                                bgcolor: '#374151',
                                                fontSize: '0.875rem',
                                                p: 2,
                                                color: 'white',
                                              },
                                              '& .MuiTooltip-arrow': {
                                                color: '#374151',
                                              },
                                            }}
                                          >
                                            <InfoIcon
                                              sx={{
                                                fontSize: '1rem',
                                                color: '#6B7280',
                                                cursor: 'help',
                                                ml: 0.5,
                                                '&:hover': {
                                                  color: '#374151',
                                                },
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      <Typography
                                        variant="caption"
                                        sx={{color: '#9CA3AF', fontSize: '0.7rem'}}
                                      >
                                        License type breakdown
                                      </Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                      <Chip
                                        label={formatLicenseTypeName(licenseType)}
                                        size="small"
                                        sx={{
                                          bgcolor: chipColors.bg,
                                          color: chipColors.text,
                                          fontWeight: 600,
                                          fontSize: '0.65rem',
                                        }}
                                      />
                                      {typeQuota.has_unlimited && (
                                        <Chip
                                          label="Unlimited"
                                          size="small"
                                          sx={{
                                            bgcolor: '#059669',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.65rem',
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>

                                  {/* Stats - Flat Layout */}
                                  <Grid container spacing={2} sx={{mb: 2.5}}>
                                    {[
                                      {
                                        label: isSipType ? 'Total Users' : 'Total Units',
                                        // Backend sends -1 for both total and available when unlimited
                                        value: typeQuota.has_unlimited
                                          ? '∞'
                                          : typeQuota.total_units || 0,
                                        icon: <LicenseIcon fontSize="small" />,
                                      },
                                      {
                                        label: isSipType ? 'Used Users' : 'Used Units',
                                        value: typeQuota.used_units || 0,
                                        icon: <PeopleIcon fontSize="small" />,
                                      },
                                      {
                                        label: isSipType ? 'Available Users' : 'Available Units',
                                        value: typeQuota.has_unlimited ? '∞' : typeRemainingUnits,
                                        icon: <CheckCircleIcon fontSize="small" />,
                                      },
                                      {
                                        label: 'Active Licenses',
                                        value: typeQuota.active_licenses_count || 0,
                                        icon: <SecurityIcon fontSize="small" />,
                                      },
                                    ].map((stat, index) => (
                                      <Grid item xs={6} sm={3} key={index}>
                                        <Box
                                          sx={{
                                            textAlign: 'center',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              justifyContent: 'center',
                                              mb: 0.75,
                                            }}
                                          >
                                            <Avatar
                                              sx={{
                                                bgcolor: '#F3F4F6',
                                                color: '#6B7280',
                                                width: 28,
                                                height: 28,
                                              }}
                                            >
                                              {stat.icon}
                                            </Avatar>
                                          </Box>
                                          <Typography
                                            variant="h5"
                                            sx={{
                                              color:
                                                index === 2 &&
                                                !typeQuota.has_unlimited &&
                                                typeRemainingUnits < 0
                                                  ? '#DC2626'
                                                  : '#111827',
                                              fontWeight: 700,
                                              mb: 0.25,
                                            }}
                                          >
                                            {stat.value}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: '#9CA3AF',
                                              fontWeight: 500,
                                              fontSize: '0.7rem',
                                            }}
                                          >
                                            {stat.label}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    ))}
                                  </Grid>

                                  {/* Usage Progress Bar */}
                                  {!typeQuota.has_unlimited && (typeQuota.total_units || 0) > 0 && (
                                    <Box>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1.5,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: '#9CA3AF',
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                          }}
                                        >
                                          Usage Progress
                                        </Typography>
                                        <Chip
                                          label={`${Math.round(typeUsagePercentage)}% used`}
                                          size="small"
                                          sx={{
                                            bgcolor: '#EFF6FF',
                                            color:
                                              typeIsOverLimit || typeIsNearlyFull
                                                ? '#DC2626'
                                                : '#3B82F6',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            height: 22,
                                          }}
                                        />
                                      </Box>

                                      <LinearProgress
                                        variant="determinate"
                                        value={Math.min(typeUsagePercentage, 100)}
                                        sx={{
                                          height: 6,
                                          borderRadius: 3,
                                          bgcolor: '#F1F5F9',
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor:
                                              typeIsOverLimit || typeIsNearlyFull
                                                ? '#DC2626'
                                                : '#93C5FD',
                                            borderRadius: 3,
                                          },
                                        }}
                                      />

                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: typeIsOverLimit ? '#DC2626' : '#6B7280',
                                          fontWeight: typeIsOverLimit ? 600 : 400,
                                          mt: 1,
                                          textAlign: 'center',
                                          display: 'block',
                                          fontSize: '0.75rem',
                                        }}
                                      >
                                        {licenseType.toLowerCase() === 'sip'
                                          ? typeRemainingUnits > 0
                                            ? `${typeRemainingUnits} user${
                                                typeRemainingUnits !== 1 ? 's' : ''
                                              } remaining`
                                            : typeIsOverLimit
                                            ? `Over limit by ${Math.abs(typeRemainingUnits)} user${
                                                Math.abs(typeRemainingUnits) !== 1 ? 's' : ''
                                              }`
                                            : 'All users are currently allocated'
                                          : typeRemainingUnits > 0
                                          ? `${typeRemainingUnits} unit${
                                              typeRemainingUnits !== 1 ? 's' : ''
                                            } remaining`
                                          : typeIsOverLimit
                                          ? `Over limit by ${Math.abs(typeRemainingUnits)} unit${
                                              Math.abs(typeRemainingUnits) !== 1 ? 's' : ''
                                            }`
                                          : 'All units are currently allocated'}
                                      </Typography>
                                    </Box>
                                  )}
                                </Card>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    ) : (
                      <Box sx={{textAlign: 'center', py: 6}}>
                        <Typography sx={{color: '#6B7280'}}>
                          No license types found. Activate a license to get started.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{textAlign: 'center', py: 6}}>
                    <Typography sx={{color: '#6B7280'}}>No license data available</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Redeem License Card */}
          <Grid item xs={12} lg={4}>
            <Card
              id="activate-license-card"
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                border: '1px solid #E5E7EB',
                height: 'fit-content',
                '&:hover': {
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <CardContent sx={{p: 2.5}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 2}}>
                  <Avatar sx={{bgcolor: '#F0FDF4', color: '#059669', width: 32, height: 32}}>
                    <AddIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{color: '#111827', fontWeight: 600, fontSize: '0.95rem'}}
                    >
                      Activate License
                    </Typography>
                    <Typography variant="body2" sx={{color: '#6B7280', fontSize: '0.8rem'}}>
                      Add new licenses to your account
                    </Typography>
                  </Box>
                </Box>

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="License Master Key"
                    placeholder="Enter your license key here..."
                    value={masterKey}
                    onChange={e => setMasterKey(e.target.value)}
                    disabled={redeemLoading}
                    multiline
                    rows={3}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        fontSize: '0.85rem',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#D1D5DB',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: '0.85rem',
                      },
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleRedeemLicense}
                    disabled={redeemLoading || !masterKey.trim()}
                    startIcon={
                      redeemLoading ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />
                    }
                    sx={{
                      bgcolor: '#3B82F6',
                      borderRadius: 2,
                      height: 40,
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      '&:hover': {
                        bgcolor: '#2563EB',
                      },
                      '&:disabled': {
                        bgcolor: '#F3F4F6',
                        color: '#9CA3AF',
                      },
                    }}
                  >
                    {redeemLoading ? 'Activating...' : 'Activate License'}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* License Details */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                border: '1px solid #E5E7EB',
                '&:hover': {
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                },
              }}
            >
              <CardContent sx={{p: 2.5}}>
                <Typography
                  variant="subtitle1"
                  sx={{color: '#111827', fontWeight: 600, mb: 2, fontSize: '0.95rem'}}
                >
                  License Details
                </Typography>

                {loading ? (
                  <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
                    <CircularProgress sx={{color: '#3B82F6'}} />
                  </Box>
                ) : licenses.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{borderRadius: 2}}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              fontSize: '0.85rem',
                              borderBottom: '2px solid #E5E7EB',
                            }}
                          >
                            Master Key Code
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              fontSize: '0.85rem',
                              borderBottom: '2px solid #E5E7EB',
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              fontSize: '0.85rem',
                              borderBottom: '2px solid #E5E7EB',
                            }}
                          >
                            Status
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              fontSize: '0.85rem',
                              borderBottom: '2px solid #E5E7EB',
                            }}
                          >
                            Units
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              fontSize: '0.85rem',
                              borderBottom: '2px solid #E5E7EB',
                            }}
                          >
                            Activated
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: '#111827',
                              fontSize: '0.85rem',
                              borderBottom: '2px solid #E5E7EB',
                            }}
                          >
                            Expires
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {licenses.map((license, index) => {
                          const statusDisplay = getStatusDisplay(license.license_status);
                          const chipColors = license.license_type
                            ? getLicenseTypeChipColor(license.license_type)
                            : {bg: '#F3F4F6', text: '#374151'};

                          return (
                            <TableRow
                              key={license.id || index}
                              sx={{
                                '&:hover': {
                                  bgcolor: '#F9FAFB',
                                },
                              }}
                            >
                              <TableCell>
                                {license.entrio_license_master_keys?.token ? (
                                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#111827',
                                        fontFamily: 'monospace',
                                        fontSize: '0.8rem',
                                        bgcolor: '#F3F4F6',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        flex: 1,
                                      }}
                                      title={license.entrio_license_master_keys.token}
                                    >
                                      {license.entrio_license_master_keys.token}
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => {
                                        navigator.clipboard.writeText(
                                          license.entrio_license_master_keys?.token || ''
                                        );
                                        message.success('Token copied to clipboard');
                                      }}
                                      sx={{
                                        minWidth: 'auto',
                                        p: 0.5,
                                        color: '#6B7280',
                                        '&:hover': {
                                          color: '#3B82F6',
                                        },
                                      }}
                                    >
                                      <ContentCopyIcon fontSize="small" />
                                    </Button>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" sx={{color: '#9CA3AF'}}>
                                    N/A
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={
                                    license.license_type
                                      ? formatLicenseTypeName(license.license_type)
                                      : 'N/A'
                                  }
                                  sx={{
                                    bgcolor: chipColors.bg,
                                    color: chipColors.text,
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={statusDisplay.color}
                                  icon={statusDisplay.icon}
                                  label={statusDisplay.label}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                  <PeopleIcon fontSize="small" sx={{color: '#6B7280'}} />
                                  <Typography
                                    variant="body2"
                                    sx={{color: '#111827', fontWeight: 600, fontSize: '0.85rem'}}
                                  >
                                    {license.units || 0}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                  <CalendarIcon fontSize="small" sx={{color: '#6B7280'}} />
                                  <Typography
                                    variant="body2"
                                    sx={{color: '#111827', fontSize: '0.85rem'}}
                                  >
                                    {license.activated_at
                                      ? formatDate(license.activated_at)
                                      : 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                  <CalendarIcon fontSize="small" sx={{color: '#6B7280'}} />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        license.license_status === 'expired'
                                          ? '#DC2626'
                                          : '#111827',
                                      fontWeight: license.license_status === 'expired' ? 600 : 400,
                                      fontSize: '0.85rem',
                                    }}
                                  >
                                    {license.license_expires_at
                                      ? formatDate(license.license_expires_at)
                                      : 'N/A'}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{textAlign: 'center', py: 4}}>
                    <Avatar
                      sx={{
                        bgcolor: '#F3F4F6',
                        color: '#9CA3AF',
                        width: 48,
                        height: 48,
                        mx: 'auto',
                        mb: 1.5,
                      }}
                    >
                      <LicenseIcon />
                    </Avatar>
                    <Typography
                      variant="subtitle1"
                      sx={{color: '#6B7280', mb: 0.5, fontSize: '0.95rem'}}
                    >
                      No licenses found
                    </Typography>
                    <Typography variant="body2" sx={{color: '#9CA3AF', fontSize: '0.8rem'}}>
                      Activate your first license using the form above
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
